export const siteMeta = {
  name: "Education Project",
  shortDescription:
    "A 501(c)(3) nonprofit and lifelong-learning ecosystem platform advancing education in its broadest sense.",
  fullDescription:
    "Education Project is a 501(c)(3) nonprofit and lifelong-learning ecosystem platform advancing formal, non-formal, and informal learning through media, programs, digital platforms, partnerships, and community initiatives.",
  trustStatement:
    "A New Hampshire 501(c)(3) advancing education through media, programs, digital platforms, partnerships, and community initiatives.",
};

export const navigationItems = [
  { label: "Home", href: "/" },
  { label: "About", href: "/about" },
  { label: "Learning Hub", href: "/learning-hub" },
  { label: "Initiatives", href: "/programs" },
  { label: "Community", href: "/community" },
  { label: "Project Support", href: "/project-support" },
  { label: "Contact", href: "/contact" },
];

export const footerNavigationGroups = [
  {
    title: "Explore",
    links: [
      { label: "Home", href: "/" },
      { label: "Learning Hub", href: "/learning-hub" },
      { label: "Programs & Initiatives", href: "/programs" },
      { label: "Media for Learning", href: "/media" },
      { label: "Stories & Insights", href: "/stories" },
    ],
  },
  {
    title: "Get Involved",
    links: [
      { label: "Community & Partnerships", href: "/community" },
      { label: "Project Support", href: "/project-support" },
      { label: "Donate", href: "/donate" },
      { label: "Contact", href: "/contact" },
    ],
  },
];

export const footerLinks = [
  { label: "Privacy Policy", href: "/privacy" },
  { label: "Terms of Use", href: "/terms" },
  { label: "Donation Policy", href: "/donation-policy" },
];

export const boardMembers = [
  "Earl Taylor, Incorporator and Director",
  "Wiliam Humphreys, Incorporator and Director",
  "Edward Moran, Incorporator and Director",
  "John Malatesta, Incorporator and Director",
  "Alex Greene, Incorporator and Director",
  "Jill Taylor, Incorporator and Director",
];

export const governanceHighlights = [
  {
    title: "Board-governed stewardship",
    body: "The corporation is governed by a Board of Directors, who also serve as its sole members.",
  },
  {
    title: "Mission-first use of funds",
    body: "No net earnings may benefit members or directors. Resources are reinvested into the organization's educational purpose.",
  },
  {
    title: "501(c)(3) charitable intent",
    body: "The organization is structured to qualify as a 501(c)(3) tax-exempt entity and must operate exclusively for charitable, scientific, literary, or educational purposes.",
  },
  {
    title: "Public-interest guardrails",
    body: "The corporation is prohibited from political campaign activity and from substantial lobbying.",
  },
];

export const organizationalValues = [
  {
    title: "Accessible learning",
    body: "Learning opportunities should welcome people across backgrounds, life stages, and economic circumstances.",
  },
  {
    title: "Public purpose",
    body: "Work is directed toward educational, cultural, and public-interest benefit rather than private gain.",
  },
  {
    title: "Stewardship",
    body: "Financial and operational decisions must support the mission and uphold nonprofit obligations.",
  },
  {
    title: "Open regard",
    body: "The organization is pursued without regard to race, creed, color, sex, or ability to pay.",
  },
];

export const ecosystemPillars = [
  {
    title: "Learning across formats",
    body: "TEP supports learning through media, programs, workshops, mentorship, and community-based experiences.",
  },
  {
    title: "Platform for initiatives",
    body: "The organization can help mission-aligned ideas grow into durable public-facing programs and projects.",
  },
  {
    title: "Partnership-centered delivery",
    body: "Schools, nonprofits, artists, practitioners, and civic partners all strengthen the learning ecosystem.",
  },
  {
    title: "Trusted nonprofit foundation",
    body: "Board governance, 501(c)(3) intent, and careful stewardship remain central to how the platform operates.",
  },
];

export const whatWeDoItems = [
  {
    title: "Media for learning",
    body: "Documentaries, digital storytelling, and public-interest media that help knowledge travel beyond a single room.",
    href: "/media",
  },
  {
    title: "Programs and workshops",
    body: "Structured experiences that help learners, educators, professionals, and communities engage with ideas in practical ways.",
    href: "/programs",
  },
  {
    title: "Digital learning platforms",
    body: "Online resources, pathways, and public-facing tools that make lifelong learning easier to access across schedules and settings.",
    href: "/learning-hub",
  },
  {
    title: "Research and innovation",
    body: "Research-informed exploration that helps new learning models, media approaches, and technology-enabled experiences serve real people.",
    href: "/learning-hub",
  },
  {
    title: "Partnerships and community initiatives",
    body: "Cross-sector collaborations, events, and public programs that connect learning to civic, creative, and local priorities.",
    href: "/community",
  },
  {
    title: "Fiscal sponsorship and project support",
    body: "Mission-aligned project leaders can explore sponsorship, infrastructure support, and strategic collaboration under nonprofit stewardship.",
    href: "/project-support",
  },
];

export const learningHubPaths = [
  {
    slug: "workshops-classes",
    title: "Workshops & Classes",
    body: "Hands-on sessions that turn ideas into practice through facilitation, discussion, and applied learning.",
    images: ["/pathway-workshops.png"],
  },
  {
    slug: "online-learning",
    title: "Online Learning",
    body: "Digital content and flexible experiences that make learning more available across schedules and locations.",
    images: ["/pathway-online.png"],
  },
  {
    slug: "creative-programs",
    title: "Creative Programs",
    body: "Storytelling, arts, and media-centered learning that invites curiosity, reflection, and self-expression.",
    images: ["/pathway-creative.png"],
  },
  {
    slug: "professional-development",
    title: "Professional Development",
    body: "Skill-building and knowledge-sharing experiences for educators, practitioners, and mission-driven leaders.",
    images: ["/pathway-professional.png"],
  },
  {
    slug: "health-wellness",
    title: "Health & Wellness",
    body: "Learning experiences that support whole-person growth, resilience, and informed well-being.",
    images: ["/pathway-health.png"],
  },
  {
    slug: "community-learning",
    title: "Community Learning",
    body: "Public programs, conversations, and place-based experiences that connect learning to everyday life.",
    images: ["/pathway-community.png"],
  },
];

export const initiativeHighlights = [
  {
    slug: "education-beyond-the-classroom",
    label: "Flagship initiative",
    title: "Education Beyond the Classroom",
    body: "A signature initiative designed to expand learning through media, community experiences, partnerships, and practical resource-sharing.",
  },
  {
    slug: "creative-learning-programs",
    label: "Program area",
    title: "Creative Learning Programs",
    body: "Initiatives that use storytelling, arts, and culture as gateways to deeper educational engagement.",
  },
  {
    slug: "community-learning-labs",
    label: "Program area",
    title: "Community Learning Labs",
    body: "Collaborative experiences that bring together learners, educators, and community partners around shared questions and local priorities.",
  },
  {
    slug: "partner-powered-curricula",
    label: "Program area",
    title: "Partner-Powered Learning",
    body: "Programs developed alongside institutions, nonprofit partners, and practitioners to expand relevance and reach.",
  },
];

export const partnerPathways = [
  {
    title: "For learners and families",
    body: "Access inviting pathways into workshops, media, public programs, and community-centered learning experiences.",
  },
  {
    title: "For educators and facilitators",
    body: "Co-create workshops, resource pathways, and public learning experiences that can serve more people.",
  },
  {
    title: "For organizations and institutions",
    body: "Partner on initiatives, events, storytelling, or sponsored programming that aligns with educational mission and community need.",
  },
  {
    title: "For donors and sponsors",
    body: "Invest in learning access, media-based education, project incubation, and public-serving nonprofit infrastructure.",
  },
];

export const communityEngagementItems = [
  {
    title: "Partnership development",
    body: "Schools, nonprofits, artists, practitioners, and civic organizations can collaborate with TEP to build meaningful learning experiences.",
  },
  {
    title: "Mentorship and dialogue",
    body: "Community conversations, guided experiences, and mentorship opportunities can help ideas move from interest to action.",
  },
  {
    title: "Events and public programming",
    body: "TEP can convene workshops, screenings, talks, and gatherings that create accessible entry points into lifelong learning.",
  },
  {
    title: "Sponsor visibility with purpose",
    body: "Sponsors can help sustain mission-aligned work while supporting public benefit, community access, and educational opportunity.",
  },
];

export const projectSupportItems = [
  {
    title: "Fiscal sponsorship",
    body: "Mission-aligned initiatives can explore fiscal sponsorship when there is strategic fit, operating capacity, and board approval.",
  },
  {
    title: "Operational infrastructure",
    body: "Projects can benefit from help with administrative structure, fundraising readiness, compliance thinking, and public-facing setup.",
  },
  {
    title: "Narrative and communications support",
    body: "TEP can help shape clearer public messaging around educational purpose, audience, and impact.",
  },
  {
    title: "Network and partnership support",
    body: "Project leaders can connect with collaborators, sponsors, and institutions that strengthen implementation and reach.",
  },
];

export const mediaFormats = [
  {
    title: "Short-form digital media",
    body: "Social and digital storytelling can introduce ideas quickly and invite broader participation into educational initiatives.",
  },
  {
    title: "Broadcast and long-form media",
    body: "Television, documentary, and professionally produced long-form work can deepen public understanding and preserve complex stories.",
  },
  {
    title: "Streaming and on-demand access",
    body: "Streaming and digital archives make it easier for audiences to learn on their own schedule and revisit materials over time.",
  },
];

export const storyHighlights = [
  {
    slug: "many-formats",
    title: "Why lifelong learning needs many formats",
    body: "A strong ecosystem lets media, facilitated experiences, and community engagement reinforce each other instead of competing.",
  },
  {
    slug: "flagship-initiative",
    title: "Education Beyond the Classroom as a platform story",
    body: "Flagship initiatives can become bridges between storytelling, public participation, and durable educational opportunity.",
  },
  {
    slug: "project-support",
    title: "What mission-aligned project support can unlock",
    body: "Fiscal sponsorship and nonprofit infrastructure can help promising ideas move from concept to community-serving reality.",
  },
];
